import type {
  CollectionStatus,
  DocumentStatus,
  DocumentType,
  PaymentAttemptStatus,
  PaymentCorrectionStatus,
  PaymentTaskStatus,
} from '@hsl-ems/contracts';

const apiBase = `${import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3000'}/api/v1`;

export interface PaymentAttemptRecord {
  id: string;
  attemptNo: number;
  status: PaymentAttemptStatus;
  processorUserId: string;
  processor: { id: string; name: string };
  payerAccountRef: string;
  bankTransactionNo: string | null;
  failureReason: string | null;
  paidAt: string | null;
  startedAt: string;
  completedAt: string | null;
  version: number;
  files: Array<{
    purpose: string;
    file: { id: string; originalName: string; status: string };
  }>;
}

export interface PaymentTaskRecord {
  id: string;
  paymentNo: string;
  sourceDocumentId: string;
  operatingOrgId: string;
  amount: string;
  payeeSnapshot: Record<string, unknown>;
  approvalSnapshot: Record<string, unknown>;
  expectedPaymentDate: string | null;
  status: PaymentTaskStatus;
  version: number;
  processingStartedAt: string | null;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
  sourceDocument: {
    id: string;
    documentNo: string;
    type: DocumentType;
    status: DocumentStatus;
    applicantUserId: string;
    requestedAmount: string;
    approvedAmount: string | null;
    payableAmount: string | null;
    description: string;
  };
  attempts: PaymentAttemptRecord[];
}

export interface RepaymentCollectionRecord {
  id: string;
  documentNo: string;
  operatingOrgId: string;
  status: DocumentStatus;
  requestedAmount: string;
  description: string;
  version: number;
  approvedAt: string | null;
  applicant: { id: string; name: string; employeeNo: string | null };
  availableLoanBalance: string;
  cashRepayment: {
    documentId: string;
    loanDocumentId: string;
    amount: string;
    note: string | null;
    collectionStatus: CollectionStatus;
    confirmedAmount: string | null;
    collectedAt: string | null;
    collectionMethod: string | null;
    referenceNo: string | null;
    collectionProofFileId: string | null;
    decisionAt: string | null;
    rejectionReason: string | null;
    version: number;
    decisionBy: { id: string; name: string } | null;
    collectionProofFile: { id: string; originalName: string; status: string } | null;
    loanDocument: {
      id: string;
      documentNo: string;
      requestedAmount: string;
      completedAt: string | null;
      loan: { plannedRepaymentDate: string } | null;
    };
  };
  files: Array<{
    purpose: string;
    file: { id: string; originalName: string; status: string; scanResult: string | null };
  }>;
}

export interface PaymentCorrectionRecord {
  id: string;
  paymentAttemptId: string;
  operatingOrgId: string;
  amount: string;
  reason: string;
  status: PaymentCorrectionStatus;
  requestedByUserId: string;
  requestedBy: { id: string; name: string };
  reviewedBy: { id: string; name: string } | null;
  reviewComment: string | null;
  requestedAt: string;
  reviewedAt: string | null;
  version: number;
  files: Array<{
    purpose: string;
    file: { id: string; originalName: string; status: string };
  }>;
  paymentAttempt: PaymentAttemptRecord & { paymentTask: PaymentTaskRecord };
}

export interface LoanOffsetRecord {
  id: string;
  offsetNo: string;
  reimbursementDocumentId: string;
  loanDocumentId: string;
  amount: string;
  status: 'PLANNED' | 'POSTED' | 'CORRECTED';
  sortOrder: number;
  postedAt: string | null;
  version: number;
  createdAt: string;
  reimbursementDocument: {
    id: string;
    documentNo: string;
    status: DocumentStatus;
    applicantUserId: string;
  };
  loanDocument: { id: string; documentNo: string };
}

export interface FundingExceptionRecord {
  id: string;
  documentId: string;
  operatingOrgId: string;
  type: 'LOAN_OFFSET_INSUFFICIENT_BALANCE';
  status: 'OPEN' | 'RETURNED' | 'RESOLVED';
  detail: {
    offsetId: string;
    offsetNo: string;
    loanDocumentId: string;
    requestedAmount: string;
    availableBalance: string;
  };
  resolutionNote: string | null;
  resolvedAt: string | null;
  version: number;
  createdAt: string;
  operatingOrg: { id: string; code: string; name: string };
  resolvedBy: { id: string; name: string } | null;
  document: {
    id: string;
    documentNo: string;
    type: DocumentType;
    status: DocumentStatus;
    version: number;
    requestedAmount: string;
    payableAmount: string | null;
    applicant: { id: string; name: string };
    reimbursementOffsets: Array<{
      id: string;
      offsetNo: string;
      amount: string;
      status: 'PLANNED' | 'POSTED' | 'CORRECTED';
      sortOrder: number;
      loanDocument: { id: string; documentNo: string };
    }>;
  };
}

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${apiBase}${path}`, {
    ...init,
    credentials: 'include',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json', ...init?.headers },
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as {
      code?: string;
      message?: string;
    } | null;
    if (response.status === 401) throw new Error('登录已失效，请重新登录');
    if (response.status === 404) throw new Error('付款任务不存在或不在授权范围内');
    if (response.status === 409) throw new Error('付款任务已被其他操作更新，请刷新后重试');
    if (response.status === 422) throw new Error('付款状态或提交资料不符合当前操作要求');
    throw new Error(body?.message ?? `请求失败（${response.status}）`);
  }
  return response.json() as Promise<T>;
}

function mutationHeaders(idempotent = false): Record<string, string> {
  return {
    'X-CSRF-Token': sessionStorage.getItem('hsl-ems-csrf') ?? '',
    'X-Request-Id': `web-${crypto.randomUUID()}`,
    ...(idempotent ? { 'Idempotency-Key': `web-${crypto.randomUUID()}` } : {}),
  };
}

export const p4Api = {
  tasks: () => api<PaymentTaskRecord[]>('/payment-tasks'),
  task: (id: string) => api<PaymentTaskRecord>(`/payment-tasks/${id}`),
  startAttempt: (taskId: string, version: number, payerAccountRef: string) =>
    api<{
      paymentTask: { id: string; status: 'PROCESSING'; version: number };
      paymentAttempt: PaymentAttemptRecord;
    }>(`/payment-tasks/${taskId}/start-attempt`, {
      method: 'POST',
      headers: mutationHeaders(true),
      body: JSON.stringify({ version, payerAccountRef }),
    }),
  createReceiptUploadIntent: (payload: {
    paymentAttemptId: string;
    originalName: string;
    mimeType: string;
    sizeBytes: number;
  }) =>
    api<{
      id: string;
      uploadUrl: string;
      expiresAt: string;
      requiredHeaders: Record<string, string>;
    }>('/files/payment-receipt-upload-intents', {
      method: 'POST',
      headers: mutationHeaders(),
      body: JSON.stringify(payload),
    }),
  createCorrectionEvidenceUploadIntent: (payload: {
    paymentAttemptId: string;
    originalName: string;
    mimeType: string;
    sizeBytes: number;
  }) =>
    api<{
      id: string;
      uploadUrl: string;
      expiresAt: string;
      requiredHeaders: Record<string, string>;
    }>('/files/payment-correction-evidence-upload-intents', {
      method: 'POST',
      headers: mutationHeaders(),
      body: JSON.stringify(payload),
    }),
  completeUpload: (id: string, sha256: string) =>
    api<{ id: string; status: 'PENDING_SCAN' }>(`/files/${id}/complete`, {
      method: 'POST',
      headers: mutationHeaders(),
      body: JSON.stringify({ sha256 }),
    }),
  file: (id: string) =>
    api<{ id: string; originalName: string; status: string; scanResult: string | null }>(
      `/files/${id}`,
    ),
  fileDownloadUrl: (id: string) =>
    api<{ url: string; expiresAt: string }>(`/files/${id}/download-url`),
  completeAttempt: (
    attemptId: string,
    payload:
      | {
          taskVersion: number;
          attemptVersion: number;
          result: 'SUCCEEDED';
          paidAt: string;
          bankTransactionNo: string;
          receiptFileId: string;
        }
      | {
          taskVersion: number;
          attemptVersion: number;
          result: 'FAILED';
          failureReason: string;
        },
  ) =>
    api(`/payment-attempts/${attemptId}/complete`, {
      method: 'POST',
      headers: mutationHeaders(true),
      body: JSON.stringify(payload),
    }),
  paymentCorrections: () => api<PaymentCorrectionRecord[]>('/payment-corrections'),
  createPaymentCorrection: (
    attemptId: string,
    payload: {
      attemptVersion: number;
      taskVersion: number;
      evidenceFileId: string;
      reason: string;
    },
  ) =>
    api<PaymentCorrectionRecord>(`/payment-attempts/${attemptId}/corrections`, {
      method: 'POST',
      headers: mutationHeaders(true),
      body: JSON.stringify(payload),
    }),
  approvePaymentCorrection: (id: string, payload: { version: number; comment?: string }) =>
    api(`/payment-corrections/${id}/approve`, {
      method: 'POST',
      headers: mutationHeaders(true),
      body: JSON.stringify(payload),
    }),
  rejectPaymentCorrection: (id: string, payload: { version: number; reason: string }) =>
    api(`/payment-corrections/${id}/reject`, {
      method: 'POST',
      headers: mutationHeaders(true),
      body: JSON.stringify(payload),
    }),
  repaymentCollections: () => api<RepaymentCollectionRecord[]>('/cash-repayment-collections'),
  loanOffsets: () => api<LoanOffsetRecord[]>('/loan-offsets'),
  fundingExceptions: () => api<FundingExceptionRecord[]>('/funding-exceptions'),
  returnFundingException: (
    id: string,
    payload: { exceptionVersion: number; documentVersion: number; reason: string },
  ) =>
    api(`/funding-exceptions/${id}/return`, {
      method: 'POST',
      headers: mutationHeaders(true),
      body: JSON.stringify(payload),
    }),
  confirmRepaymentCollection: (
    documentId: string,
    payload: {
      documentVersion: number;
      collectionVersion: number;
      confirmedAmount: string;
      collectedAt: string;
      collectionMethod: string;
      referenceNo: string;
      proofFileId: string;
    },
  ) =>
    api(`/cash-repayments/${documentId}/confirm-collection`, {
      method: 'POST',
      headers: mutationHeaders(true),
      body: JSON.stringify(payload),
    }),
  rejectRepaymentCollection: (
    documentId: string,
    payload: { documentVersion: number; collectionVersion: number; reason: string },
  ) =>
    api(`/cash-repayments/${documentId}/reject-collection`, {
      method: 'POST',
      headers: mutationHeaders(true),
      body: JSON.stringify(payload),
    }),
};
